import classNames from 'classnames';
import { memo, useCallback, useState } from 'react';

import { Plus, TriangleDown } from '@air/next-icons';
import { Button } from '@air/primitive-button';
import { IconButton } from '@air/primitive-icon-button';
import { useAirModal } from '@air/provider-modal';

import { LibraryCreateModal } from '~/components/LibraryBeta/LibraryCreateModal/LibraryCreateModal';
import { WorkspaceSidebarLibrariesMoreDropdown } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarLibrariesMoreDropdown';
import { WorkspaceSidebarSectionHeader } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionHeader';
import { useWorkspaceSidebarSectionStore } from '~/components/v2/components/workspace/workspace-sidebar/WorkspaceSidebarSectionStoreContext';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { canCreateLibrary } from '~/utils/permissions/libraryPermissions';

export const WorkspaceSidebarLibrariesHeader = memo(() => {
  const [open, setOpen] = useState(false);
  const collapsed = useWorkspaceSidebarSectionStore((store) => store.collapsed);
  const toggleCollapsed = useWorkspaceSidebarSectionStore((store) => store.toggleCollapsed);
  const [showLibraryCreateModal] = useAirModal(LibraryCreateModal);
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();

  const canCreateLibraries = canCreateLibrary(workspacePermissions);

  const onCreateLibrary = useCallback(() => {
    showLibraryCreateModal();
  }, [showLibraryCreateModal]);

  return (
    <WorkspaceSidebarSectionHeader>
      <Button
        appearance="ghost"
        className="text-grey-9 hover:bg-grey-5"
        color="grey"
        onClick={toggleCollapsed}
        size="small"
        suffix={<TriangleDown className={classNames('size-4', 'transition-transform', { '-rotate-90': collapsed })} />}
      >
        Libraries
      </Button>
      <div
        className={classNames(
          'flex items-center gap-1 transition-opacity group-focus-within/workspace-sidebar-section-header:opacity-100 group-hover/workspace-sidebar-section-header:opacity-100',
          // Forces the div to be visible when the dropdown is open to avoid ensure that users is
          // aware the dropdown is associated with the actions in the header.
          open ? 'opacity-100' : 'opacity-0',
        )}
      >
        <WorkspaceSidebarLibrariesMoreDropdown onOpenChange={setOpen} />
        {canCreateLibraries && (
          <IconButton appearance="ghost" color="grey" label="Create new library" onClick={onCreateLibrary} size="small">
            <Plus className="size-4" />
          </IconButton>
        )}
      </div>
    </WorkspaceSidebarSectionHeader>
  );
});

WorkspaceSidebarLibrariesHeader.displayName = 'WorkspaceSidebarLibrariesHeader';
